<script setup>
import {
  markRaw, onMounted, ref, toValue,
} from 'vue';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import {
  pick, pipe, assoc, not, isEmpty, prop,
} from 'ramda';
import { useI18n } from 'vue-i18n';
import AccountFormField from '@/components/forms/AccountFormField.vue';
import AccountFormFieldSelect from '@/components/forms/AccountFormFieldSelect.vue';
import AccountFormTextArea from '@/components/forms/AccountFormTextArea.vue';
import InPlaceAlert from '@/components/InPlaceAlert.vue';
import useCallEndPointWith from '@/services/backend/useCallEndPointWith.js';
import { helpOptions } from '@/services/formUtilities.js';
import { useGlobalState } from '@/store/user.store.js';
import user from '@/services/user.js';
import WithLoader from '@/components/WithLoader.vue';

const { t } = useI18n();
const { isAuthenticated } = useGlobalState();

const validationSchema = markRaw(
  yup.object({
    email: yup.string().email().lowercase().required()
      .label(t('registerView.accountForm.labels.email')),
    confirmEmail: yup.string().email().required().oneOf([yup.ref('email')], 'Emails must match')
      .label(t('registerView.accountForm.labels.confirmEmail')),
    name: yup.string().required().label(t('registerView.accountForm.labels.name')),
    helpType: yup.string().required().label(t('contact.contactForm.labels.helpType')),
    subject: yup.string().required().max(128).label(t('contact.contactForm.labels.subject')),
    message: yup.string().required().max(500).label(t('contact.contactForm.labels.message')),
  }),
);

const { defineField, handleSubmit } = useForm({ validationSchema });

const [email, emailAttrs] = defineField('email');
const [confirmEmail, confirmEmailAttrs] = defineField('confirmEmail');
const [name, nameAttrs] = defineField('name');
const [helpType, helpTypeAttrs] = defineField('helpType');
const [subject, subjectAttrs] = defineField('subject');
const [message, messageAttrs] = defineField('message');

const { callEndpoint, userFriendlyError, isLoading } = useCallEndPointWith('contact');

const createContactFormData = pipe(
  pick(['email', 'name', 'helpType', 'subject', 'message']),
);

const {
  userInfo,
  fetchUserInfo,
  isLoadingGet,
  errorGet,
} = user();

onMounted(async () => {
  if (isAuthenticated()) {
    await fetchUserInfo();
    if (not(isEmpty(errorGet.value))) {
      console.error('Error retrieving account data:', errorGet.value);
    }
    email.value = prop('email', toValue(userInfo));
    confirmEmail.value = prop('email', toValue(userInfo));
    name.value = `${prop('firstName', toValue(userInfo))} ${prop('lastName', toValue(userInfo))}`;
  }
});
const successMessage = ref('');
const submit = handleSubmit(async (values) => {
  const formData = createContactFormData(values);
  await callEndpoint(assoc('method', 'POST', { request: formData }));
  successMessage.value = t('contact.contactForm.successMessage');
});
</script>

<template>
  <div class="mt-24 pb-24 pt-12">
    <WithLoader :is-loading="isLoadingGet">
      <WithLoader :is-loading="isLoading">
        <form @submit.prevent="submit">
          <div class="max-w-4xl mx-auto px-16 py-8 mt-8 base-gradient text-primary rounded-lg">
            <h2 class="text-2xl">
              {{ $t('contact.contactForm.header') }}
            </h2>
            <div class="px-4 pb-36 pt-8 sm:px-6">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3">
                <InPlaceAlert
                  :message="userFriendlyError"
                  type="error"
                  class="col-span-2"
                  @close-alert="userFriendlyError = ''"
                />
                <InPlaceAlert
                  v-if="successMessage"
                  :message="successMessage"
                  type="success"
                  class="col-span-2"
                  @close-alert="successMessage = ''"
                />
                <AccountFormField
                  id="email"
                  :label="$t('contact.contactForm.labels.email')"
                  :is-required="true"
                  v-model="email"
                  v-bind="emailAttrs"
                  input-type="email"
                  auto-complete-type="email"
                />
                <AccountFormField
                  id="confirmEmail"
                  :label="$t('contact.contactForm.labels.confirmEmail')"
                  :is-required="true"
                  v-model="confirmEmail"
                  v-bind="confirmEmailAttrs"
                  input-type="email"
                  auto-complete-type="email"
                />
                <AccountFormField
                  id="name"
                  :label="$t('contact.contactForm.labels.name')"
                  :is-required="true"
                  v-model="name"
                  v-bind="nameAttrs"
                  input-type="text"
                  auto-complete-type="name"
                />
                <AccountFormFieldSelect
                  id="helpType"
                  :label="$t('contact.contactForm.labels.helpType')"
                  :is-required="true"
                  v-model="helpType"
                  v-bind="helpTypeAttrs"
                  :options="helpOptions"
                />
                <AccountFormField
                  id="subject"
                  :label="$t('contact.contactForm.labels.subject')"
                  :is-required="true"
                  v-model="subject"
                  v-bind="subjectAttrs"
                  input-type="text"
                  auto-complete-type="subject"
                />
                <AccountFormTextArea
                  id="message"
                  :label="$t('contact.contactForm.labels.message')"
                  :is-required="true"
                  v-model="message"
                  v-bind="messageAttrs"
                  class="min-h-60"
                />
              </div>
            </div>
          </div>
          <div class="flex flex-row justify-end max-w-4xl mx-auto mt-8">
            <button
              type="submit"
              class="w-full mt-4 px-14 py-2 btn-cta sm:order-last sm:w-auto"
            >
              {{ $t('contact.contactForm.submit') }}
            </button>
          </div>
        </form>
      </WithLoader>
    </WithLoader>
  </div>
</template>
